import React, { useState, useEffect } from 'react';
import {
  User,
  Mail,
  Phone,
  Globe,
  CreditCard,
  Save,
  Crown,
  Calendar,
  Clock,
  AlertCircle,
  HelpCircle,
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Badge } from '@/components/ui/Badge';
import { PageLoader } from '@/components/ui/Spinner';
import { useProfile, useUpdateProfile } from '@/hooks/useProfile';
import { useTranslation } from '@/i18n';

const languageOptions = [
  { value: 'pt-BR', label: 'Portugues (Brasil)' },
  { value: 'en-GB', label: 'English (UK)' },
];

function formatDate(dateString: string | null | undefined, locale: string) {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString(locale, {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
}

function formatPhone(phone: string) {
  if (phone.startsWith('55') && phone.length >= 12) {
    const country = phone.slice(0, 2);
    const area = phone.slice(2, 4);
    const part1 = phone.slice(4, 9);
    const part2 = phone.slice(9);
    return `+${country} ${area} ${part1}-${part2}`;
  }
  if (phone.startsWith('44') && phone.length >= 11) {
    const country = phone.slice(0, 2);
    const part1 = phone.slice(2, 6);
    const part2 = phone.slice(6);
    return `+${country} ${part1} ${part2}`;
  }
  return `+${phone}`;
}

function getSubscriptionStatusVariant(status: string) {
  switch (status) {
    case 'active':
      return 'success' as const;
    case 'trialing':
      return 'info' as const;
    case 'past_due':
      return 'warning' as const;
    case 'canceled':
    case 'expired':
      return 'danger' as const;
    default:
      return 'default' as const;
  }
}

export const SettingsPage: React.FC = () => {
  const { t, locale } = useTranslation();
  const { data, isLoading, isError } = useProfile();
  const updateMutation = useUpdateProfile();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [language, setLanguage] = useState('pt-BR');
  const [hasChanges, setHasChanges] = useState(false);

  // Initialize form with profile data
  useEffect(() => {
    if (data?.user) {
      setName(data.user.name || '');
      setEmail(data.user.email || '');
      setLanguage(data.user.language || 'pt-BR');
    }
  }, [data]);

  // Track changes
  useEffect(() => {
    if (data?.user) {
      const changed =
        name !== (data.user.name || '') ||
        email !== (data.user.email || '') ||
        language !== (data.user.language || 'pt-BR');
      setHasChanges(changed);
    }
  }, [name, email, language, data]);

  const handleSave = async () => {
    await updateMutation.mutateAsync({
      name: name || undefined,
      email: email || undefined,
      language: language as 'pt-BR' | 'en-GB',
    });
    setHasChanges(false);
  };

  if (isLoading) {
    return <PageLoader message={t.app.settings.loading} />;
  }

  if (isError || !data) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-12 h-12 mx-auto text-red-500 mb-4" />
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
          {t.app.settings.error}
        </h2>
        <p className="text-gray-500 dark:text-gray-400">
          {t.app.settings.errorDesc}
        </p>
      </div>
    );
  }

  const { user, subscription } = data;

  return (
    <div className="space-y-6 max-w-3xl">
      {/* Page header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{t.app.settings.title}</h1>
        <p className="text-gray-500 dark:text-gray-400 mt-1">
          {t.app.settings.subtitle}
        </p>
      </div>

      {/* Profile Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="w-5 h-5" />
            {t.app.settings.profile.title}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Phone (read-only) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t.app.settings.profile.phone}
              </label>
              <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 dark:bg-secondary-700/50 rounded-lg border border-gray-200 dark:border-secondary-600">
                <Phone className="w-4 h-4 text-gray-400" />
                <span className="text-gray-900 dark:text-white">
                  {formatPhone(user.phoneNumber)}
                </span>
                <Badge variant="secondary" className="ml-auto">
                  {t.app.settings.profile.verified}
                </Badge>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {t.app.settings.profile.phoneHint}
              </p>
            </div>

            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t.app.settings.profile.name}
              </label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t.app.settings.profile.namePlaceholder}
                leftIcon={<User className="w-4 h-4" />}
              />
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t.app.settings.profile.email}
              </label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t.app.settings.profile.emailPlaceholder}
                leftIcon={<Mail className="w-4 h-4" />}
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {t.app.settings.profile.emailHint}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Preferences Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe className="w-5 h-5" />
            {t.app.settings.preferences.title}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Language */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t.app.settings.preferences.language}
              </label>
              <Select
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                options={languageOptions}
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {t.app.settings.preferences.languageHint}
              </p>
            </div>

            {/* Timezone (read-only for now) */}
            {user.timezone && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t.app.settings.preferences.timezone}
                </label>
                <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 dark:bg-secondary-700/50 rounded-lg border border-gray-200 dark:border-secondary-600">
                  <Clock className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-900 dark:text-white">{user.timezone}</span>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Subscription Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="w-5 h-5" />
            {t.app.settings.subscription.title}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {subscription ? (
            <div className="space-y-4">
              {/* Plan & Status */}
              <div className="flex items-center justify-between p-4 bg-gradient-to-r from-primary-50 to-primary-100 dark:from-primary-900/20 dark:to-primary-800/20 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-primary-500 rounded-full flex items-center justify-center">
                    <Crown className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white">
                      {t.app.settings.subscription.plan} {t.app.settings.subscription.plans[subscription.plan.toLowerCase() as keyof typeof t.app.settings.subscription.plans] || subscription.plan}
                    </h3>
                    <Badge variant={getSubscriptionStatusVariant(subscription.status)}>
                      {t.app.settings.subscription.status[subscription.status as keyof typeof t.app.settings.subscription.status] || subscription.status}
                    </Badge>
                  </div>
                </div>
              </div>

              {/* Dates */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {subscription.trialEndsAt && (
                  <div className="flex items-start gap-3">
                    <Calendar className="w-5 h-5 text-gray-400 mt-0.5" />
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">{t.app.settings.subscription.trialEndsAt}</p>
                      <p className="font-medium text-gray-900 dark:text-white">
                        {formatDate(subscription.trialEndsAt, locale)}
                      </p>
                    </div>
                  </div>
                )}

                {subscription.currentPeriodEnd && (
                  <div className="flex items-start gap-3">
                    <Calendar className="w-5 h-5 text-gray-400 mt-0.5" />
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">{t.app.settings.subscription.nextBilling}</p>
                      <p className="font-medium text-gray-900 dark:text-white">
                        {formatDate(subscription.currentPeriodEnd, locale)}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="pt-4 border-t border-gray-200 dark:border-secondary-700 space-y-3">
                <div className="flex items-center gap-3">
                  {!['pro', 'professional', 'enterprise', 'vip'].includes(subscription.plan.toLowerCase()) && (
                    <Button
                      variant="primary"
                      onClick={() => (window.location.href = '/subscribe')}
                    >
                      {t.app.settings.subscription.upgrade}
                    </Button>
                  )}
                  <Button
                    variant="secondary"
                    onClick={() => (window.location.href = '/feedback')}
                    leftIcon={<HelpCircle className="w-4 h-4" />}
                  >
                    {t.app.settings.subscription.contactSupport}
                  </Button>
                </div>
                <button
                  onClick={() => (window.location.href = '/feedback?type=account_deletion')}
                  className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                >
                  {t.app.settings.subscription.cancel}
                </button>
              </div>
            </div>
          ) : (
            <div className="text-center py-6">
              <Crown className="w-12 h-12 mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                {t.app.settings.subscription.noSubscription}
              </h3>
              <p className="text-gray-500 dark:text-gray-400 mb-4">
                {t.app.settings.subscription.noSubscriptionDesc}
              </p>
              <Button variant="primary" onClick={() => (window.location.href = '/subscribe')}>
                {t.app.settings.subscription.viewPlans}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Account Info */}
      <Card>
        <CardHeader>
          <CardTitle>{t.app.settings.account.title}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
            <p>{t.app.settings.account.id}: {user.id}</p>
            <p>{t.app.settings.account.createdAt}: {formatDate(user.createdAt, locale)}</p>
          </div>
        </CardContent>
      </Card>

      {/* Save Button */}
      {hasChanges && (
        <div className="sticky bottom-4 flex justify-end">
          <Button
            variant="primary"
            size="lg"
            onClick={handleSave}
            isLoading={updateMutation.isPending}
            leftIcon={<Save className="w-5 h-5" />}
            className="shadow-lg"
          >
            {t.app.settings.saveChanges}
          </Button>
        </div>
      )}
    </div>
  );
};

export default SettingsPage;
